'use client';

import { useState } from 'react';
import { Button } from '@/atoms/Button/Button';
import { Container } from '@/atoms/Container/Container';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/atoms/DropdownMenu/DropdownMenu';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/atoms/Sheet/Sheet';
import { Typography } from '@/atoms/Typography/Typography';
import { useIsMobile } from '@/hooks/useIsMobile/useIsMobile';
import { useRequireAuth } from '@/hooks/useRequireAuth/useRequireAuth';
import { useResourceSaveTargets } from '@/hooks/useResourceSaveTargets/useResourceSaveTargets';
import { cn } from '@/libs/utils/utils';
import { postActionsCountVariants } from '@/organisms/PostActionsBar/PostActionsBar.variants';
import {
  SavePickerContent,
  SaveTriggerIcon,
  type SaveTriggerIconState,
} from '@/organisms/PostSavePicker/PostSavePicker';
import { RESOURCE_ACTION_BUTTON_CLASS_NAME } from './ResourceActions.constants';

interface ResourceSavePickerProps {
  /** The external resource's link exactly as written: the collection item's `uri`. */
  uri: string;
  /**
   * How many public collections hold the link (Pinakes `card.collections`); no number shows when it
   * is absent or 0.
   */
  collectionsCount?: number | null;
}

/**
 * Adds an external resource to the viewer's own collections, or takes it out: the post save
 * picker's collection list and "New collection" for a link (its bookmark is a button of its own
 * beside this one). The trigger shows how many public collections hold the link. Signed out, it
 * asks to sign in.
 */
export function ResourceSavePicker({ uri, collectionsCount }: ResourceSavePickerProps) {
  const isMobile = useIsMobile();
  const { requireAuth } = useRequireAuth();
  const [open, setOpen] = useState(false);
  const targets = useResourceSaveTargets(uri, { isPickerOpen: open });
  const count = collectionsCount && collectionsCount > 0 ? collectionsCount : null;
  const isSaved = targets.collections.some((collection) => collection.isSaved);
  const triggerIconState: SaveTriggerIconState = isSaved ? 'saved' : count ? 'collected' : 'default';

  const trigger = (
    <Button
      variant="ghost"
      size="sm"
      className={cn(RESOURCE_ACTION_BUTTON_CLASS_NAME, !count && 'w-10')}
      aria-label={count ? `Add to collection (in ${count} public collections)` : 'Add to collection'}
      data-testid="resource-collections-button"
    >
      <SaveTriggerIcon state={triggerIconState} />
      {count && (
        <Typography
          as="span"
          overrideDefaults
          className={postActionsCountVariants({ variant: 'default' })}
          data-testid="resource-collections-count"
        >
          {count}
        </Typography>
      )}
    </Button>
  );

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setOpen(false);
      return;
    }
    requireAuth(() => setOpen(true));
  };

  const contentProps = {
    collections: targets.collections,
    isCollectionsLoading: targets.isCollectionsLoading,
    isCreatingCollection: targets.isCreatingCollection,
    hasMoreCollections: targets.hasMoreCollections,
    isCollectionsLoadingMore: targets.isCollectionsLoadingMore,
    loadMoreCollections: targets.loadMoreCollections,
    otherCollectionIds: [],
    isOtherCollectionsLoading: false,
    toggleCollection: targets.toggleCollection,
    createCollectionWithPost: targets.createCollectionWithItem,
  };

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetTrigger asChild>{trigger}</SheetTrigger>
        <SheetContent
          side="bottom"
          aria-describedby={undefined}
          className="flex max-h-dvh flex-col gap-0 rounded-t-xl border-border bg-popover"
        >
          <SheetHeader>
            <SheetTitle>{'Save link'}</SheetTitle>
          </SheetHeader>
          <Container overrideDefaults className="min-h-0 flex-1 overflow-y-auto">
            <SavePickerContent layout="sheet" {...contentProps} />
          </Container>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="max-h-[var(--radix-dropdown-menu-content-available-height)] w-70 overflow-y-auto"
      >
        <SavePickerContent layout="dropdown" {...contentProps} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
